import { type CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";

import { ColocaDateField } from "@/shared/components/forms/ColocaDateField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPhoneField } from "@/shared/components/forms/ColocaPhoneField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";

import {
  documentLengthMessageKey,
  documentTypeLabelKey,
  documentTypesFor,
  isDocumentNumberLengthValid,
} from "../../constants/clientDocumentTypes.constants";
import type { CreateClientBody, IndividualIdentityRequest } from "../../types/clients-api.types";
import { MAX_EMAIL } from "../../utils/emailValidation";
import { countryItems, normalizePhone, PERSON_NAME_REGEX } from "./client-form.utils";

const buildPersonSchema = (
  phoneError: string,
  birthdateError: string,
  nameFormatError: string,
  documentLengthError: (code: string) => string | undefined,
) =>
  z
    .object({
      email: z.string().email().max(MAX_EMAIL),
      first_name: z.string().min(1).max(255).regex(PERSON_NAME_REGEX, nameFormatError),
      last_name: z.string().min(1).max(255).regex(PERSON_NAME_REGEX, nameFormatError),
      birthdate: z
        .string()
        .min(1)
        .regex(/^\d{4}-\d{2}-\d{2}$/, birthdateError),
      gender: z.enum(["M", "F", "OTHER"]),
      nationality: z.string().length(3),
      country: z.string().length(3),
      identification_type: z.string().min(1),
      identification_number: z.string().min(1).max(100),
      phone: z.string().min(1),
      phone_country: z.string().length(2),
    })
    .superRefine((data, ctx) => {
      if (!isValidPhoneNumber(data.phone, data.phone_country as CountryCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["phone"], message: phoneError });
      }
      // Document types with a fixed length (CPF) are checked here only; the backend validates the character
      // set but no length, so this is the only place a mistyped CPF is caught before it is stored.
      if (!isDocumentNumberLengthValid(data.identification_type, data.identification_number)) {
        const message = documentLengthError(data.identification_type);
        if (message) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["identification_number"],
            message,
          });
        }
      }
    });

type PersonValues = z.infer<ReturnType<typeof buildPersonSchema>>;

const PERSON_DEFAULTS: PersonValues = {
  email: "",
  first_name: "",
  last_name: "",
  birthdate: "",
  gender: "M",
  nationality: "COL",
  country: "COL",
  identification_type: "CC",
  identification_number: "",
  phone: "",
  phone_country: "",
};

type PersonClientFormProps = {
  formId: string;
  /** Receives the validated body; the host modal performs the request and owns success/error toasts. */
  onSubmit: (body: CreateClientBody) => void | Promise<void>;
  /** Parent organization for the subclient flow; omitted for an org user's own client (inferred from JWT). */
  organizationId?: string;
};

export function PersonClientForm({ formId, onSubmit, organizationId }: PersonClientFormProps) {
  const { t } = useTranslations("clients");
  const countryLabel = useCountryLabel();
  const countryOptions = useMemo(() => countryItems(countryLabel), [countryLabel]);
  const documentLengthError = useCallback(
    (code: string) => {
      const key = documentLengthMessageKey(code);
      return key ? t(key) : undefined;
    },
    [t],
  );
  const personSchema = useMemo(
    () =>
      buildPersonSchema(
        t("validation_phone_required"),
        t("validation_birthdate_invalid"),
        t("validation_name_letters_only"),
        documentLengthError,
      ),
    [t, documentLengthError],
  );
  const form = useZodForm(personSchema, PERSON_DEFAULTS);

  // Document types are country-dependent on the backend (`@IsValidDocumentType`), so the
  // `identification_type` options follow the selected document country. A client of type person shows the
  // "personal" category (Personal ID) — company tax IDs (fiscal) never appear here.
  const documentCountry = form.watch("country");
  const idTypeCodes = useMemo(() => documentTypesFor(documentCountry, "personal"), [documentCountry]);
  const idTypeItems = useMemo(
    () => idTypeCodes.map((code) => ({ value: code, label: t(documentTypeLabelKey(code)) })),
    [idTypeCodes, t],
  );

  // Keep the selected document type valid for the chosen country. When the type is actually replaced the
  // number typed for the old one is cleared too — a Colombian CC left behind under a Brazilian RG passes
  // every check and is stored as the wrong document. A type that survives the country change (`CE`, shared by
  // COL and PER) keeps its number: nothing about it became wrong.
  useEffect(() => {
    const current = form.getValues("identification_type");
    if (idTypeCodes.length > 0 && !(idTypeCodes as readonly string[]).includes(current)) {
      form.setValue("identification_type", idTypeCodes[0], { shouldValidate: false });
      form.setValue("identification_number", "", { shouldValidate: false });
      // Otherwise the previous document's error stays pinned under the now-empty field.
      form.clearErrors("identification_number");
    }
  }, [idTypeCodes, form]);

  const handleSubmit = async (values: PersonValues) => {
    const digits = normalizePhone(values.phone);
    const identity: IndividualIdentityRequest = {
      email: values.email.trim(),
      phone: digits,
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      birthdate: values.birthdate,
      gender: values.gender,
      country: values.country,
      nationality: values.nationality,
      identification_type: values.identification_type,
      identification_number: values.identification_number.trim(),
    };
    const body: CreateClientBody = {
      type: "individual",
      ...(organizationId ? { organization_id: organizationId } : {}),
      identity,
      email: values.email.trim(),
      phone: `+${digits}`,
      country: values.country,
    };

    await onSubmit(body);
  };

  return (
    <ColocaForm form={form} onSubmit={handleSubmit} id={formId} className="space-y-6">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <ColocaInputField name="first_name" label={t("modal_label_first_name")} />
        <ColocaInputField name="last_name" label={t("modal_label_last_name")} />
        <ColocaInputField name="email" label={t("modal_label_email")} type="email" />
        <ColocaPhoneField name="phone" label={t("modal_label_phone")} countryFieldName="phone_country" />
        <ColocaDateField
          name="birthdate"
          label={t("modal_label_birthdate")}
          maxDate={new Date()}
          fromYear={1900}
          toYear={new Date().getFullYear()}
        />
        <ColocaSelectField
          name="gender"
          label={t("modal_label_gender")}
          items={[
            { value: "M", label: t("gender_male") },
            { value: "F", label: t("gender_female") },
            { value: "OTHER", label: t("gender_other") },
          ]}
        />
        <ColocaSelectField name="nationality" label={t("modal_label_nationality")} items={countryOptions} />
        <ColocaSelectField name="country" label={t("modal_label_id_country")} items={countryOptions} />
        <ColocaSelectField
          name="identification_type"
          label={t("modal_label_identification_type")}
          items={idTypeItems}
        />
        <ColocaInputField name="identification_number" label={t("modal_label_identification_number")} />
      </div>
    </ColocaForm>
  );
}
